import { useEffect, useRef } from "react";

const PROFILES = {
  idle: { amp: 0.08, speed: 0.6, layers: 2, jitter: 0 },
  listening: { amp: 0.28, speed: 1.1, layers: 3, jitter: 0.35 },
  reflecting: { amp: 0.18, speed: 0.5, layers: 5, jitter: 0.05 },
  speaking: { amp: 0.42, speed: 1.8, layers: 4, jitter: 0.5 },
};

export const Waveform = ({ state = "idle", className = "", height = 220 }) => {
  const ref = useRef(null);
  const cur = useRef({ ...PROFILES.idle });

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const w = canvas.clientWidth;
      canvas.width = w * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const target = PROFILES[state] || PROFILES.idle;
      const c = cur.current;
      c.amp += (target.amp - c.amp) * 0.04;
      c.speed += (target.speed - c.speed) * 0.04;
      c.layers += (target.layers - c.layers) * 0.04;
      c.jitter += (target.jitter - c.jitter) * 0.04;

      const w = canvas.clientWidth;
      const h = height;
      ctx.clearRect(0, 0, w, h);
      t += 0.012 * c.speed;

      const layers = Math.round(c.layers);
      for (let l = 0; l < layers; l++) {
        const phase = t * (1 + l * 0.18) + l * 1.3;
        const alpha = 0.9 - l * (0.65 / Math.max(layers, 1));
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, `rgba(224,122,95,0)`);
        grad.addColorStop(0.25, `rgba(224,122,95,${alpha})`);
        grad.addColorStop(0.6, `rgba(244,162,97,${alpha})`);
        grad.addColorStop(1, `rgba(244,162,97,0)`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = l === 0 ? 2.2 : 1.2;
        ctx.shadowBlur = l === 0 ? 18 : 0;
        ctx.shadowColor = "rgba(224,122,95,0.45)";
        ctx.beginPath();
        for (let x = 0; x <= w; x += 2) {
          const n = x / w;
          const env = Math.sin(n * Math.PI);
          const jit = c.jitter * Math.sin(x * 0.11 + t * 7 + l) * 0.35;
          const y =
            h / 2 +
            env *
              h *
              c.amp *
              (Math.sin(n * 6.5 + phase) * 0.6 +
                Math.sin(n * 13 - phase * 1.4) * 0.28 +
                Math.sin(n * 3 + phase * 0.6 + l) * 0.35 +
                jit);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [state, height]);

  return (
    <canvas
      ref={ref}
      data-testid="waveform-canvas"
      data-state={state}
      className={`w-full block ${className}`}
      style={{ height }}
    />
  );
};
